// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import myw from 'myWorld-base';
import Dialog from 'myWorld/uiComponents/dialog';
import addLayerControlHtml from 'text!html/addLayerControl.html';
import './defineLayerControl';
import 'jquery-ui';
import 'jquery-ui-touch';

const templatesHtml = $(addLayerControlHtml),
    addLayersDialog = templatesHtml.filter('#add-layers-dialog-template').html(),
    addLayersRow = templatesHtml.filter('#add-layers-row-template').html();

myw.AddLayerControl = Dialog.extend('AddLayerControl', {
    innerTemplate: _.template(addLayersDialog),

    events: {
        'keyup .text-layer-search': 'filterLayers'
    },

    options: {
        editPrivateLayerOnPhone: false, //For simplicity of the phone UI
        modal: true,
        autoOpen: false,
        width: 460,
        height: 'auto',
        resizable: false,
        position: { my: 'center', at: 'top+160', of: window, collision: 'fit' },
        title: '{:add_layer_title}',
        closeText: '{:close_tooltip}',
        buttons: {
            Close: {
                text: '{:close_btn}',
                class: 'right',
                click() {
                    this.close();
                }
            },
            Create: {
                text: '{:create_layer_btn}',
                class: 'create-layer-btn',
                click() {
                    this.defineLayerDialog();
                }
            }
        },
        open(event, ui) {
            $(this)
                .find('.text-layer-search')
                .blur();
        }
    },
    /**
     * @class Control that allows users to add layers to the map/layerManager.
     * The list of layers includes the layers that are accessible by the user but are not added to the map yet.
     * @param  {LayerControl}   owner   Owner of self
     * @constructs
     * @extends {Control}
     */
    initialize(layerManager, options) {
        //call control constructor
        myw.Dialog.prototype.initialize.call(this, options);
        this.editPrivateLayers = !(myw.app.isHandheld && !this.options.editPrivateLayerOnPhone);
        if (myw.isNativeApp || !this.editPrivateLayers)
            this.options.buttons = _(this.options.buttons).omit('Create');

        this.layerManager = layerManager;
    },

    render() {
        //build UI
        const maxHeight = $(window).height() - 100;
        this.options.maxHeight = maxHeight;
        this.options.contents = this.innerTemplate();

        myw.Dialog.prototype.render.call(this);
        this.translate(this.$el);
        this.addLayerTable = this.$('.add-layer-list');

        this.layerManager.system.userHasPermission('addPrivateLayers').then(hasPerm => {
            if (hasPerm) {
                this.renderTabs();
            }
            this.show().then(() => {
                this.$el.dialog('open');
            });
        });
    },

    /**
     * Renders tabs to separate out the private layers from the system layers
     */
    renderTabs() {
        if (this.tabs) return;

        this.tabs = $('<div>', { id: 'add-layer-tabs' });
        this.$el
            .dialog('widget')
            .children('.ui-dialog-titlebar')
            .after(this.tabs);
        //get tab definitions
        const tabs = [
            {
                id: 'system-layers-tab',
                title: this.msg('system_tab_title')
            },
            {
                id: 'private-layers-tab',
                title: this.msg('private_tab_title')
            }
        ];

        this.tabControl = new myw.TabControl(this, {
            el: this.tabs,
            tabs: tabs,
            initialTab: tabs[0].id
        });

        // Add a top border to the tab buttons
        this.tabControl._tabButtons.addClass('top-bordered');
        this.tabControl.on('change', tabId => {
            this.show();
        });
    },

    /**
     * Obtains the layer and layer groups that should be shown in the addLayerControl.
     * Opens the add layer dialog
     * @return {Promise} Resolved when the layers are ready to be added to the control
     */
    show() {
        const currentLayers = _.filter(
                this.layerManager.layerList,
                layer => layer.type === 'layer'
            ),
            currentGroups = _.filter(
                this.layerManager.layerList,
                layer => layer.type === 'layer_group'
            ),
            currentLayerNames = _.pluck(currentLayers, 'layer_name'),
            currentGroupNames = _.pluck(currentGroups, 'layer_name');

        //asynchronously get all of the available overlay layers in the system
        const layersPromise = this.layerManager
            .getLayerListLayersAndGroups()
            .then(layersAndGroups => {
                this.layerListItems = _.sortBy(layersAndGroups.layers, layer =>
                    layer.layerDef.name.toLowerCase()
                );

                const availableLayerDefs = _.filter(
                    this.layerListItems,
                    item => !_.contains(currentLayerNames, item.layer_name)
                );

                if (this.tabControl && this.tabControl.currentTabId === 'private-layers-tab') {
                    const privateLayers = _.filter(availableLayerDefs, item =>
                        item.layerDef.hasOwnProperty('owner')
                    );
                    this._showPrivateLayers(privateLayers, currentLayers);
                } else {
                    const systemLayers = _.filter(
                        availableLayerDefs,
                        item => !item.layerDef.hasOwnProperty('owner')
                    );

                    const allLayerGroups = _.sortBy(layersAndGroups.layerGroups, 'layer_name'),
                        availableLayerGroups = _.filter(
                            allLayerGroups,
                            group => !_.contains(currentGroupNames, group.layer_name)
                        );

                    this._showAvailableLayers(systemLayers, availableLayerGroups);
                }
            });

        return layersPromise;
    },

    /**
     * Filter the table of layers when a key is pressed(released)
     */
    filterLayers(ev) {
        const filterText = this.$(ev.currentTarget).val();
        const rows = this.addLayerTable.children();
        _.each(rows, element => {
            const row = this.$(element),
                re = new RegExp(filterText, 'gi');

            if (row.text().search(re) > -1) row.show();
            else row.hide();
        });
    },

    defineLayerDialog(layerDef) {
        let options = {
            layerDef,
            onChangeCallback: this.show.bind(this)
        };
        new myw.DefineLayerControl(this.layerManager, options);
    },

    /**
     * Displays the layers and layer groups that user can add to the map
     * @param  {array<string>} availableLayerDefs  Layers that are not in the user's layer list
     * @private
     */
    _showAvailableLayers(availableLayerDefs, availableLayerGroups) {
        this.addLayerTable.empty();

        // if there are available layers get them, if not let the user know they are using all available layers
        if (availableLayerDefs.length > 0 || availableLayerGroups.length > 0) {
            this._appendLayers(availableLayerDefs);
            this._appendGroups(availableLayerGroups);
            this.showNoLayersMode(false);
        } else {
            this.showNoLayersMode(true);
        }

        // Resize the add layer control on window resize
        $(window)
            .resize(() => {
                this.$el.css({
                    'max-height': $(window).height() - 185,
                    'overflow-y': 'auto'
                });
            })
            .resize();

        this.$el
            .dialog('widget')
            .find('.ui-dialog-buttonpane')
            .find('.create-layer-btn')
            .hide();
    },

    _showPrivateLayers(privateLayers, currentLayers) {
        this.addLayerTable.empty();

        const processedPrivateLayers = _.map(privateLayers, layer => {
            const currentLayerDefs = _.pluck(currentLayers, 'layerDef');
            layer['isAvailable'] = !_.contains(currentLayerDefs, layer.layerDef);
            return layer;
        });
        // if there are available layers get them, if not let the user know they are using all available layers
        if (privateLayers.length > 0) {
            this._appendLayers(processedPrivateLayers);
            this.showNoLayersMode(false);
        } else {
            this.showNoLayersMode(true);
        }

        // Resize the add layer control on window resize
        $(window)
            .resize(() => {
                this.$el.css({
                    'max-height': $(window).height() - 185,
                    'overflow-y': 'auto'
                });
            })
            .resize();
        this.$el
            .dialog('widget')
            .find('.ui-dialog-buttonpane')
            .find('.create-layer-btn')
            .show();
    },

    /**
     * Creates a view for the layers and adds then to the addLayerControl
     * @param  {array<string>} availableLayerNames  Names of layers already in the users layer list
     * @private
     */
    _appendLayers(availableLayerDefs) {
        _.each(availableLayerDefs, layerListItem => {
            const layerDef = layerListItem.layerDef;
            const properties = {
                name: layerDef.display_name || layerDef.name,
                description: this.layerManager.getLayerItemDescription(layerDef),
                thumbnail: layerDef.thumbnail,
                isAvailable: layerListItem.isAvailable,
                layerDef
            };

            let layerRow;
            if (this.tabControl && this.tabControl.currentTabId === 'private-layers-tab') {
                layerRow = new PrivateLayerView({
                    control: this,
                    model: properties
                });
            } else {
                layerRow = new AvailableLayerView({
                    control: this,
                    model: properties
                });
            }
            this.addLayerTable.append(layerRow.el);
        });
    },

    /**
     * Creates a view for the layer-groups and adds then to the addLayerControl
     * @private
     */
    _appendGroups(availableLayerGroups) {
        _.each(availableLayerGroups, group => {
            const groupRow = new AvailableLayerView({
                control: this,
                model: group
            });
            this.addLayerTable.append(groupRow.el);
        });
    },

    /**
     * Displays a message that there are no layers to show
     * @param  {boolean} noLayers Decides whether to show this mode or not
     * @private
     */
    showNoLayersMode(noLayers) {
        this.$('.text-layer-search')[noLayers ? 'hide' : 'show']();
        this.$('.no-available-layer-rows')[noLayers ? 'show' : 'hide']();
    },

    /**
     * Asks the layerManager to add the layer group to the map
     * @param {AvailableLayerView} group group's view that the add action came from
     */
    addGroupToMap(group) {
        this.layerManager.addLayerGroupToList(group.model, group.model.subLayers);

        this._afterAdd(group, true);
    },
    /**
     * Asks the layerManager to add the layer to the map
     * @param {AvailableLayerView} layer layer's view that triggered the add action
     */
    addLayerToMap(layerView) {
        // add the layer
        const layerListItem = _.findWhere(this.layerListItems, {
            layer_name: layerView.model.layerDef.id
        });

        // put the layers on the map, setting it to visible
        const targetLayer = this.layerManager.addLayerFromDef(layerListItem.layerDef, true);

        this._afterAdd(layerView, !!targetLayer);
    },
    /**
     * Shows a 'no more layers to add' message if its was the last item to add.
     * If the layer/group that was sent to be added wasn't a valid one, dislays an error message.
     * @param  {AvailableLayerView} layer         group/layer's view that triggered the add action
     * @param  {Boolean}            isValidLayer  If the layer that was sent to be aded was a valid layer
     * @private
     */
    _afterAdd(layer, isValidLayer) {
        if (isValidLayer) {
            //If this is the last row, show the no available layers message
            if (layer.$el.siblings().length === 0) {
                this.showNoLayersMode(true);
            }
            layer.remove();
        } else {
            //ENH display this message in the add layer control so it's (more) visible
            myw.app.message(this.msg('add_layer_error'), 2000, 2000, 'layers');
        }
    }
});

const AvailableLayerView = Backbone.View.extend({
    innerTemplate: _.template(addLayersRow),

    genericEvents: {
        'click .add-layer-button': 'addButtonHandler',
        'click .add-layer-cell': 'addButtonHandler',
        'click .expandLayerGroup': 'expandLayerGroup'
    },

    isEditable: false,

    initialize(options) {
        this.control = options.control;
        this.model = options.model;
        const events = _.extend(this.events || {}, this.genericEvents);
        this.render();

        this.delegateEvents(events);
    },

    render() {
        if (typeof this.model.isAvailable === 'undefined') this.model['isAvailable'] = true;
        this.setElement(this.innerTemplate(_.extend(this.model, { isEditable: this.isEditable })));
        this.control.translate(this.$el);
    },

    expandLayerGroup(ev) {
        ev.stopPropagation(); //so the click on expand button is not bubbled up to the row which adds the layer to the panel
        this.$('.sub-layer-list').toggle();
        this.$(ev.currentTarget).toggleClass('expanded');
    },

    addButtonHandler() {
        if (this.model.subLayers) this.control.addGroupToMap(this);
        else this.control.addLayerToMap(this);
    },

    remove() {
        this.$el.remove();
    },

    edit(e) {
        e.preventDefault();
        const layerDef = this.model.layerDef;
        if (layerDef) {
            this.control.defineLayerDialog(layerDef);
        }
    }
});

const PrivateLayerView = AvailableLayerView.extend({
    events: {
        'click .edit-btn': 'edit'
    },

    render() {
        this.isEditable = this.control.editPrivateLayers;
        this.model.description = `${this.control.msg('owner')}: ${
            this.model.layerDef.owner
        } | ${this.control.msg('type')}: ${this.model.layerDef.datasource_spec.type}`;
        this.model.thumbnail = ''; // We don't want to show the thumbnails for private layers since they are all same
        AvailableLayerView.prototype.render.call(this);
    }
});

/**
 * @typedef layerGroup

 * @property {integer}                        id                     Id of the layer
 * @property {string}                         name                   Name of the layer
 * @property {string}                         description            Description for the layer-group
 * @property {boolean}                        exclusive              Whether the group's layers can only be exclusively selected or not
 * @property {string}                         thumbnail              Thumbnail image source path
 * @property {array<string>}                  layers                 Names of layers that are assigned to this group
 */

export default myw.AddLayerControl;
